import { Box, Button, Grid, Stack, Typography } from '@mui/material'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { getPageTitle, Images } from '@utils/index'
import PageSEO from '@/components/SEO/PageSEO'

type AboutProps = {
  embedded?: boolean
}

const btnSx = {
  padding: 1,
  borderRadius: 5,
  minWidth: { xs: '100%', sm: 160 },
}

const About = ({ embedded = false }: AboutProps) => {
  const [t] = useTranslation()

  useEffect(() => {
    if (!embedded) {
      document.title = getPageTitle(t('about.title'))
    }
  }, [t, embedded])

  return (
    <Box sx={{ width: '100%' }}>
      {!embedded && (
        <PageSEO
          title={t('about.title') + ' - Minh Nguyen'}
          description={
            t('about.meta.description') ||
            'Learn more about Minh Nguyen, a fullstack developer with experience in web development, design, and digital transformation.'
          }
          path="/about"
          keywords={
            t('about.meta.keywords')?.split(', ') || [
              'about',
              'bio',
              'fullstack developer',
              'web development',
              'design'
            ]
          }
        />
      )}
      <Typography
        variant="h4"
        component={embedded ? 'h2' : 'h1'}
        sx={{
          textAlign: 'center',
          fontWeight: 'medium',
          py: 2,
          fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
        }}>
        {t('about.title')}
      </Typography>
      <Grid container spacing={{ xs: 2, md: 3 }}>
        <Grid item xs={12} md={4}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: { xs: 'center', md: 'end' }
            }}>
            <Box
              component="img"
              src={Images.home.avtNCM}
              alt="avt-bio"
              width={315}
              height={420}
              loading="lazy"
              decoding="async"
              sx={{
                width: '100%',
                maxWidth: { xs: 260, sm: 320, md: '100%' },
                height: 'auto',
                aspectRatio: '3 / 4',
                objectFit: 'cover',
                display: 'block',
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={12} md={8}>
          <Box>
            <Typography
              variant="h4"
              component="h3"
              sx={{ fontSize: { xs: '1.35rem', sm: '1.75rem', md: '2.125rem' } }}>
              {t('about.my-bio')}
            </Typography>
            <Typography
              sx={{
                marginTop: 2,
                fontSize: { xs: '0.9rem', sm: '1rem' },
                lineHeight: 1.7
              }}>
              {t('about.my-summary')}
              <br />
              <br />
              {t('about.career-objective')}
              <br />
              <br />
              {t('about.my-design')}
              <br />
              <br />
              <strong>{t('about.skills')}</strong>
            </Typography>
          </Box>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            mt={3}
            alignItems={{ xs: 'stretch', sm: 'center' }}>
            <Button
              variant="contained"
              color="secondary"
              fullWidth={false}
              sx={{ ...btnSx, width: { xs: '100%', sm: 'auto' } }}>
              {t('about.hire-me')}
            </Button>
            <Button
              component="a"
              href="/CV_NCM_VI.pdf"
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              color="secondary"
              sx={{ ...btnSx, width: { xs: '100%', sm: 'auto' } }}>
              {t('about.download-cv')}
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  )
}

export default About
